import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Layout/Sidebar';
import ClientService from '../../services/clientService';
import { motion, AnimatePresence } from 'framer-motion';

export default function ClientList() {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await ClientService.getClients();
        console.log('Client response:', response);

        const clientArray = Array.isArray(response.clients)
          ? response.clients
          : [];

        setClients(clientArray);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleView = (clientId) => {
    navigate(`/clients/view/${clientId}`);
  };

  const handleEdit = (clientId) => {
    navigate(`/clients/edit/${clientId}`);
  };

  const handleDelete = (clientId) => {
    setClientToDelete(clientId);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (clientToDelete) {
      try {
        setIsLoading(true);
        await ClientService.deleteClient(clientToDelete);
        setClients(clients.filter(client => client.id !== clientToDelete));
        setIsLoading(false);
      } catch (error) {
        console.error('Error deleting client:', error);
        setIsLoading(false);
      }
      setIsModalOpen(false);
      setClientToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />

      {/* Main content area with left margin for sidebar */}
      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        <div className="max-w-8xl mx-auto mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white bg-black p-4 rounded shadow text-center">
            Client List
          </h1>
        </div>

        <div className="max-w-8xl mx-auto bg-white p-4 md:p-6 rounded shadow overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-black"></div>
            </div>
          ) : (
            <>
              <h2 className="text-xl md:text-2xl font-bold mb-4">All Registered Clients</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border border-gray-300">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-2 py-2 md:px-4 md:py-2 border text-center">#</th>
                      <th className="px-2 py-2 md:px-4 md:py-2 border">Name</th>
                      <th className="px-2 py-2 md:px-4 md:py-2 border hidden sm:table-cell">Email</th>
                      <th className="px-2 py-2 md:px-4 md:py-2 border">Phone</th>
                      <th className="px-2 py-2 md:px-4 md:py-2 border hidden md:table-cell">Address</th>
                      <th className="px-2 py-2 md:px-4 md:py-2 border text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-gray-500">
                          No clients found.
                        </td>
                      </tr>
                    ) : (
                      clients.map((client, index) => (
                        <tr key={client.id} className="hover:bg-gray-50">
                          <td className="px-2 py-2 md:px-4 md:py-2 border text-center">{index + 1}</td>
                          <td className="px-2 py-2 md:px-4 md:py-2 border font-medium">{client.name}</td>
                          <td className="px-2 py-2 md:px-4 md:py-2 border hidden sm:table-cell">{client.email}</td>
                          <td className="px-2 py-2 md:px-4 md:py-2 border">{client.phone}</td>
                          <td className="px-2 py-2 md:px-4 md:py-2 border hidden md:table-cell">{client.address}</td>
                          <td className="px-2 py-2 md:px-4 md:py-2 border text-center">
                            <div className="flex flex-col sm:flex-row sm:space-x-2 justify-center">
                              <button
                                onClick={() => handleView(client.id)}
                                className="px-2 py-1 bg-black text-white rounded hover:bg-blue-600 mb-1 sm:mb-0 text-sm"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleEdit(client.id)}
                                className="px-2 py-1 bg-black text-white rounded hover:bg-yellow-600 mb-1 sm:mb-0 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(client.id)}
                                className="px-2 py-1 bg-black text-white rounded hover:bg-red-600 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div 
              initial={{opacity:0}}
              animate={{opacity:1}}
              exit={{ opacity:0}}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale:0.9, opacity: 0 }}
                animate={{ scale:1, opacity: 1 }}
                exit={{ scale:0.9, opacity: 0 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full"
              >
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p className="mb-6">Are you sure you want to delete this client? This action cannot be undone.</p>
                <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-2 sm:space-y-0">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setClientToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}